import React, { useState } from 'react';
import FlipIcon from '@mui/icons-material/FlipCameraAndroid';

import { TooltipIconButton } from '@paragon/client-tools';
import { degreesToSlope, slopeToDegrees } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, forceArray, PartialPartial } from '@paragon/utilities';

import {
  NumericField,
  numericFieldDefaultFormatValue,
  numericFieldDefaultParseValue,
  NumericFieldProps,
} from './NumericField';

type DisplayFormat = 'degrees' | 'slope';
const DEFAULT_DISPLAY_FORMAT: DisplayFormat = 'degrees';

function cycleDisplayFormat(format: DisplayFormat) {
  switch (format) {
    case 'degrees':
      return 'slope';

    case 'slope':
      return 'degrees';

    default:
      assertExhaustiveSwitch(format);
  }
}

function getEndAdornmentText(format: DisplayFormat) {
  switch (format) {
    case 'degrees':
      return 'deg';

    case 'slope':
      return '/ 12';

    default:
      assertExhaustiveSwitch(format);
  }
}

interface OwnProps {
  readonly defaultDisplayFormat?: DisplayFormat;
  readonly disableSwitchingDisplayFormat?: boolean;
}

export type AngleFieldProps<
  TAllowNull extends boolean = false,
  TAllowNullSubmittal extends boolean = false,
  TAllowIndeterminate extends boolean = false,
> = PartialPartial<
  NumericFieldProps<TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>,
  'formatValue' | 'parseValue'
> &
  OwnProps;

export function AngleField<
  TAllowNull extends boolean = false,
  TAllowNullSubmittal extends boolean = false,
  TAllowIndeterminate extends boolean = false,
>(props: AngleFieldProps<TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>) {
  const { defaultDisplayFormat, disableSwitchingDisplayFormat, ...numericFieldProps } = props;

  const numberOfDecimals = numericFieldProps.numberOfDecimals ?? 2;
  const padWithZeros = numericFieldProps.padWithZeros;

  const [displayFormat, setDisplayFormat] = useState<DisplayFormat>(defaultDisplayFormat ?? DEFAULT_DISPLAY_FORMAT);

  return (
    <NumericField<TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>
      parseValue={(input) => {
        switch (displayFormat) {
          case 'degrees':
            return numericFieldDefaultParseValue(input);

          case 'slope':
            const parsed = numericFieldDefaultParseValue(input);

            return parsed.valid
              ? { valid: true as const, value: inputSlopeToDegrees(parsed.value!) }
              : { valid: false as const };

          default:
            assertExhaustiveSwitch(displayFormat);
        }
      }}
      formatValue={(value) => {
        const formatOptions = { numberOfDecimals, padWithZeros };

        switch (displayFormat) {
          case 'degrees':
            return numericFieldDefaultFormatValue(value, formatOptions);

          case 'slope':
            return numericFieldDefaultFormatValue(degreesToDisplaySlope(value), formatOptions);

          default:
            assertExhaustiveSwitch(displayFormat);
        }
      }}
      {...numericFieldProps} // spread after format-related props to allow overriding
      endAdornment={[
        getEndAdornmentText(displayFormat),

        disableSwitchingDisplayFormat ? null : (
          <TooltipIconButton
            title="Change input format"
            size="small"
            disabled={numericFieldProps.disabled}
            onClick={() => {
              setDisplayFormat(cycleDisplayFormat);
            }}
          >
            <FlipIcon />
          </TooltipIconButton>
        ),

        ...forceArray(numericFieldProps.endAdornment),
      ]}
    />
  );
}

function degreesToDisplaySlope(valueInDegrees: number) {
  // We display a 90 degree angle as 90/12 because infinite numbers don't work with text boxes.
  if (valueInDegrees === 90) return 90;
  if (valueInDegrees === -90) return -90;

  return degreesToSlope(valueInDegrees);
}

function inputSlopeToDegrees(value: number) {
  // Allow a slope of 90/12 to be entered to represent a 90 degree angle.
  if (value === 90) return 90;
  if (value === -90) return -90;

  return slopeToDegrees(value);
}
